import * as _ from 'lodash';
import { Dispatch } from 'react-redux';
import { AuthActionCreators, IApplicationState } from '..';
import {
    IBaseResponse, IRecipientResponse, IDonorResponse, IListItem, ISingleEpletMmRisk,
    IExpertSystemElements, IExpertSystemOrder, IExpertSystemRegistration, IExpertSystemRecipient, IExpertSystemDonor, IExpertSystemOrderSummary, IExpertSystemWaitTimes,
    IExpertSystemOrderByRecipient
} from '@nkr/models/index';

export interface IExpertSystemElementsResponse {
    data: {
        success: boolean,
        results: IExpertSystemElements,
        message: string
    }
}

export interface IExpertSystemOrderResponse {
    data: {
        success: boolean,
        results: IExpertSystemOrder,
        message: string
    }
}

export interface IExpertSystemRegistrationResponse {
    data: {
        success: boolean,
        results: IExpertSystemRegistration,
        message: string
    }
}

export interface IExpertSystemRecipientResponse {
    data: {
        success: boolean,
        results: IExpertSystemRecipient[],
        message: string
    }
}

export interface IExpertSystemDonorResponse {
    data: {
        success: boolean,
        results: IExpertSystemDonor[],
        message: string
    }
}

export interface ILookupDataResponse {
    data: {
        success: boolean,
        results: {
            genderList: IListItem[],
            stateList: IListItem[]
        },
        message: string
    }
}

export interface IExpertSystemOrderSummaryResponse {
    data: {
        success: boolean,
        results: IExpertSystemOrderSummary[],
        message: string
    }
}

export interface IExpertSystemEpletRiskResponse {
    data: {
        success: boolean,
        results: ISingleEpletMmRisk,
        message: string
    }
}

export interface IExpertSystemWaitTimeResponse {
    data: {
        success: boolean,
        results: IExpertSystemWaitTimes,
        message: string
    }
}

export interface IExpertSystemOrdersByRecipientResponse {
    data: {
        success: boolean,
        message: string,
        results: IExpertSystemOrderByRecipient[]
    }
}

export const ActionCreators = {
    getExpertSystemOrder: (orderId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var options = {
            url: __API__ + `/expert-system/get/${orderId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    updateComments: (orderId: number, comments: string) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var options = {
            url: __API__ + `/expert-system/update-comments`,
            data: {
                orderId,
                comments
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    getExpertSystemWaitTimes: (recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemWaitTimeResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-wait-times/${recipientId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemWaitTimeResponse) => {
            return Promise.resolve(response);
        });
    },

    getExpertSystemOrdersByCenter: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderSummaryResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-by-center/${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemOrderSummaryResponse) => {
            return Promise.resolve(response);
        });
    },

    getExpertSystemOrdersByRecipient: (recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrdersByRecipientResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-by-recipient/${recipientId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getEpletMismatchRiskByDonorRecipient: (donorId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemEpletRiskResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-eplet-match/${donorId}/${recipientId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemEpletRiskResponse) => {
            return Promise.resolve(response);
        });
    },

    getExpertSystemElements: (donorId: number, recipientId: number) => async (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemElementsResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-expert-system-elements/${donorId}/${recipientId}`
        };

        return await dispatch(AuthActionCreators.apiGet(options));
    },

    getExpertSystemOrderByDonorRecipient: (donorId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-by-donor-recipient/${donorId}/${recipientId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    getExpertSystemStart: (registrationId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemRegistrationResponse> => {
        var options = {
            url: __API__ + `/expert-system/start/${registrationId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemRegistrationResponse) => {
            return Promise.resolve(response);
        });
    },

    getLookupData: () => (dispatch: Dispatch<IApplicationState>): Promise<ILookupDataResponse> => {
        var options = {
            url: __API__ + `/expert-system/lookup-data`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: ILookupDataResponse) => {
            return Promise.resolve(response);
        });
    },

    updateExpertSystemOrder: (orderId: number, statusId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var options = {
            url: __API__ + `/expert-system/update-status/${orderId}/${statusId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    placeExpertSystemOrder: (donorId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var options = {
            url: __API__ + `/expert-system/place-orders/${donorId}/${recipientId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    placeRecipientOrder: (orderId: number, recipientId: number, shipKit: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var options = {
            url: __API__ + `/expert-system/place-recipient-order/${orderId}/${recipientId}/${shipKit}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    processRecipientForAnalysis: (orderId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var options = {
            url: __API__ + `/expert-system/process-recipient-for-analysis/${orderId}/${recipientId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    getRecipientsForExpertSystem: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemRecipientResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-recipients/${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemRecipientResponse) => {
            return Promise.resolve(response);
        });
    },

    getDonorsForExpertSystem: (centerId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemDonorResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-donors/${centerId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemDonorResponse) => {
            return Promise.resolve(response);
        });
    },

    printRequisition: (orderId: number, entityType: number) => (dispatch: Dispatch<IApplicationState>) => {
        let options = {
            url: __API__ + '/expert-system/print-requisition',
            data: {
                orderId,
                entityType
            },
            responseType: 'blob'
        }

        return dispatch(AuthActionCreators.apiPost(options));
    },

    updateRecipient: (data: { recipientId: number, alias: string, firstName: string, lastName: string, email: string, centerId: number, address1: string, address2: string, city: string, state: string, postalCode: string, birthDate: string, gender: string, phone: string }) => (dispatch: Dispatch<IApplicationState>): Promise<IRecipientResponse> => {
        var options = {
            url: __API__ + '/expert-system/update-recipient',
            data
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IRecipientResponse) => {
            return Promise.resolve(response);
        });
    },

    updateDonor: (data: { donorId: number, alias: string, firstName: string, lastName: string, centerId: number, address1: string, address2: string, city: string, state: string, postalCode: string, birthDate: string, recipientId: number, gender: string, registrationId: number }) => (dispatch: Dispatch<IApplicationState>): Promise<IDonorResponse> => {
        var options = {
            url: __API__ + '/expert-system/update-donor',
            data
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IDonorResponse) => {
            return Promise.resolve(response);
        });
    },

    getRecipient: (recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IRecipientResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-recipient/${recipientId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IRecipientResponse) => {
            return Promise.resolve(response);
        });
    },

    getDonor: (donorId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IDonorResponse> => {
        var options = {
            url: __API__ + `/expert-system/get-donor/${donorId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IDonorResponse) => {
            return Promise.resolve(response);
        });
    },

    doCompatiblePair: (donorId: number, recipientId: number, direct: boolean, surgeryDate: string) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var options = {
            url: __API__ + `/expert-system/do-compatible-pair`,
            data: {
                donorId,
                recipientId,
                direct,
                surgeryDate
            }
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    doStandardVoucher: (donorId: number, recipientId: number) => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options = {
            url: __API__ + `/expert-system/do-standard-voucher/${donorId}/${recipientId}`
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },

    /* NOTE: This superSubmit doesnt exist, this is retained for future knowledge
    superSubmit: (
        donorData: { donorId: number, alias: string, firstName: string, lastName: string, centerId: number, address1: string, address2: string, city: string, state: string, postalCode: string, birthDate: string, recipientId: number, gender: string, registrationId: number },
        recipientData: { recipientId: number, alias: string, firstName: string, lastName: string, centerId: number, address1: string, address2: string, city: string, state: string, postalCode: string, birthDate: string, gender: string }
    ) => (dispatch: Dispatch<IApplicationState>): Promise<IExpertSystemOrderResponse> => {
        var data = {donorData, recipientData}
        var options = {
            url: __API__ + '/expert-system/super-update',
            data
        };

        return dispatch(AuthActionCreators.apiPost(options)).then((response: IExpertSystemOrderResponse) => {
            return Promise.resolve(response);
        });
    },*/

    testExpertSystem: () => (dispatch: Dispatch<IApplicationState>): Promise<IBaseResponse> => {
        var options = {
            url: __API__ + '/expert-system/test'
        };

        return dispatch(AuthActionCreators.apiGet(options)).then((response: IBaseResponse) => {
            return Promise.resolve(response);
        });
    }
};
